import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/api';
import './Login.css';

/**
 * Identifier-first sign-in for admin/support users (feedback round 3, #1).
 * Step 1 asks for the e-mail; step 2 offers the methods that can actually
 * succeed for that account: the password always, Windows Hello only when the
 * account is eligible AND has an enrolled passkey. This prevents the old trap
 * where the browser surfaced another account's passkey and the ceremony could
 * only fail. Employees/customers sign in via their personal mail link instead.
 */
const AdminLogin = () => {
  const { login, loginWithWebauthn } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'methods'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [methods, setMethods] = useState({ password: true, webauthn: false });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const routeUser = (user) => {
    if (!user || !user.userType) {
      setError('Invalid user data received. Please contact support.');
      return;
    }
    if (user.isFirstLogin === true) {
      navigate('/create-password');
      return;
    }
    switch (user.userType) {
      case 'admin': navigate('/admin/dashboard'); break;
      case 'employee': navigate('/employee/calendar'); break;
      case 'customer': navigate('/customer/dashboard'); break;
      case 'support': navigate('/support'); break;
      default: setError(`Unknown user type: ${user.userType}`);
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError(t('auth.login.emailRequired', { defaultValue: 'Enter your e-mail first' }));
      return;
    }
    try {
      setBusy(true);
      setError(null);
      const m = await authService.loginMethods(email);
      setMethods(m || { password: true, webauthn: false });
      setStep('methods');
    } catch (err) {
      // Method detection is a convenience — fall through to password-only.
      setMethods({ password: true, webauthn: false });
      setStep('methods');
    } finally {
      setBusy(false);
    }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!password) {
      setError(t('auth.login.passwordRequired', { defaultValue: 'Password is required' }));
      return;
    }
    try {
      setBusy(true);
      setError(null);
      const user = await login(email, password);
      routeUser(user);
    } catch (err) {
      setError(err.code === 'LOGIN_LINK_REQUIRED' ? t('auth.access.passwordMoved') : (err.message || 'Login failed. Please check your credentials.'));
    } finally {
      setBusy(false);
    }
  };

  const handleWebauthn = async () => {
    try {
      setBusy(true);
      setError(null);
      const user = await loginWithWebauthn(email);
      routeUser(user);
    } catch (err) {
      setError(t('auth.adminLogin.helloFailed', {
        defaultValue: 'Windows Hello sign-in did not complete — use your password below. If Windows Hello is not set up on this device yet, sign in with your password and enrol it under Settings → Security.',
      }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-container">
      <h1 className="login-title">
        {t('auth.adminLogin.title', { defaultValue: 'Administrator sign-in' })}
      </h1>

      {error && <div className="login-alert" role="alert">{error}</div>}

      {step === 'email' && (
        <form className="login-form" onSubmit={handleContinue}>
          <div className="form-group">
            <label htmlFor="admin-email" className="form-label">{t('auth.login.email', { defaultValue: 'Email' })}</label>
            <input
              type="email"
              id="admin-email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              autoComplete="username"
              autoFocus
            />
          </div>
          <button type="submit" className="submit-button" disabled={busy}>
            {busy
              ? t('auth.adminLogin.checking', { defaultValue: 'Checking…' })
              : t('auth.adminLogin.continue', { defaultValue: 'Continue' })}
          </button>
        </form>
      )}

      {step === 'methods' && (
        <>
          <p style={{ textAlign: 'center', margin: '0 0 1rem', color: 'var(--ve-ink-600)', fontSize: '0.95rem' }}>
            {email}{' '}
            <button
              type="button"
              onClick={() => { setStep('email'); setError(null); setPassword(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--act-primary)', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              {t('auth.adminLogin.change', { defaultValue: 'change' })}
            </button>
          </p>

          {methods.webauthn && (
            <button
              type="button"
              className="submit-button"
              onClick={handleWebauthn}
              disabled={busy}
              style={{ marginBottom: '1rem' }}
            >
              {t('auth.adminLogin.hello', { defaultValue: 'Sign in with Windows Hello' })}
            </button>
          )}

          <form className="login-form" onSubmit={handlePassword}>
            <div className="form-group">
              <label htmlFor="admin-password" className="form-label">{t('auth.login.password', { defaultValue: 'Password' })}</label>
              <input
                type="password"
                id="admin-password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                autoFocus={!methods.webauthn}
              />
            </div>
            <button type="submit" className="submit-button" disabled={busy}>
              {busy ? t('auth.login.signingIn', { defaultValue: 'Signing in…' }) : t('auth.login.signIn', { defaultValue: 'Sign in' })}
            </button>
          </form>

          {!methods.webauthn && (
            <p style={{ textAlign: 'center', margin: '1rem 0 0', color: 'var(--ve-ink-500)', fontSize: '0.8rem', lineHeight: 1.5 }}>
              {t('auth.adminLogin.helloHint', {
                defaultValue: 'Windows Hello appears here once it is enrolled for this account (sign in with your password, then Settings → Security).',
              })}
            </p>
          )}
        </>
      )}

      <Link to="/forgot-password" className="forgot-password-link">
        {t('auth.login.forgotPassword')}
      </Link>
      <Link to="/login" className="forgot-password-link">
        {t('auth.adminLogin.backToLogin', { defaultValue: 'Back to the normal sign-in page' })}
      </Link>
    </div>
  );
};

export default AdminLogin;
